import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactFlow, { Background, Controls, MiniMap, useNodesState, useEdgesState } from 'reactflow'
import 'reactflow/dist/style.css'
import { MasteryNode } from './MasteryNode'
import { SkillMasteryNode } from './SkillMasteryNode'
import { DomainGroupNode } from './DomainGroupNode'
import { RoutedEdge } from '../shared/RoutedEdge'
import { buildGraphLayout, STATUS_COLORS } from './knowledgeMapUtils'
import { createLogger } from '../../utils/logger'

const logger = createLogger('KnowledgeMapCanvas')

const nodeTypes = {
  masteryNode: MasteryNode,
  skillNode: SkillMasteryNode,
  domainGroup: DomainGroupNode
}
const edgeTypes = { routedEdge: RoutedEdge }

// Saved manual node positions are persisted per (storage key, layout mode, graph flavor) so a
// student's "Edit layout" tweaks survive a reload — and the skill graph's positions never clash
// with the full graph's (different node sets under the same layout mode). Keyed by
// skillGroupKey → {x,y}. Mirrors the admin canvas. `layoutStorageKey` is opt-in: callers that
// don't pass it (e.g. the educator map) get no persistence or overlay.
const modeKey = (base, mode, isSkillGraph) =>
  `${base}:layout:${mode}${isSkillGraph ? ':skill' : ''}`
const loadSavedLayout = (base, mode, isSkillGraph) => {
  if (!base) return {}
  try {
    const raw = localStorage.getItem(modeKey(base, mode, isSkillGraph))
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export default function KnowledgeMapCanvas({
  nodes,
  filters,
  searchQuery = '',
  layoutMode = 'domain',
  edgeVisibility = 'prerequisites',
  layoutStorageKey = null,
  editable = false,
  layoutVersion = 0,
  selectedNodeId = null,

  onNodeHover,
  onNodeClick,
  onPaneClick,
  onLayoutError
}) {
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([])
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([])
  const [isLayouting, setIsLayouting] = useState(false)
  // In-memory cache of saved node positions for the current (storage key, layout mode). Populated
  // from localStorage once per layout (re)build, updated in-place on each drag-stop, then flushed.
  const layoutCacheRef = useRef({})

  // Skill-graph mode is inferred from the nodes themselves (buildSkillGraphNodes tags them), so
  // callers don't need a new prop; it namespaces layout persistence below.
  const isSkillGraph = useMemo(() => nodes.some((n) => n.isSkillNode), [nodes])

  // Run async ELK layout when nodes / layout mode change (layoutVersion bumps force a re-layout, e.g.
  // after "Reset layout" clears saved positions).
  useEffect(() => {
    if (!nodes.length) {
      setRfNodes([])
      setRfEdges([])
      return
    }

    let cancelled = false
    setIsLayouting(true)

    buildGraphLayout(nodes, { groupBy: layoutMode })
      .then(({ flowNodes, flowEdges, groupNodes }) => {
        if (cancelled) return
        // Overlay any student-saved manual positions for this layout on top of ELK (no-op when no
        // layoutStorageKey is provided).
        const saved = loadSavedLayout(layoutStorageKey, layoutMode, isSkillGraph)
        layoutCacheRef.current = { ...saved }
        const positioned = flowNodes.map((n) =>
          saved[n.id] ? { ...n, position: { ...saved[n.id] } } : n
        )
        setRfNodes([...groupNodes, ...positioned])
        setRfEdges(flowEdges)
        setIsLayouting(false)
      })
      .catch((err) => {
        if (cancelled) return
        logger.error('Graph layout failed:', err)
        setIsLayouting(false)
        onLayoutError?.(err)
      })

    return () => {
      cancelled = true
    }
  }, [nodes, layoutMode, layoutVersion, layoutStorageKey, isSkillGraph, setRfNodes, setRfEdges])

  // Apply filters/search (as opacity) + selected highlight
  const filteredNodes = useMemo(() => {
    // `hasTutorPractice` is an optional dimension used by the educator/class map ('all'|'with'|
    // 'without'); the student map omits it (treated as 'all').
    const practiceFilter =
      filters.hasTutorPractice && filters.hasTutorPractice !== 'all'
        ? filters.hasTutorPractice
        : null
    const hasFilters =
      filters.statuses.length || filters.domain || filters.difficulty || practiceFilter
    const hasSearch = searchQuery && searchQuery.length >= 2
    const searchLower = hasSearch ? searchQuery.toLowerCase() : ''
    return rfNodes.map((node) => {
      if (node.type === 'domainGroup') return node
      const mastery = node.data.mastery || { status: 'UNVISITED' }
      const statusMatch = !filters.statuses.length || filters.statuses.includes(mastery.status)
      const domainMatch = !filters.domain || node.data.domain === filters.domain
      // Skill nodes span all difficulties, so a difficulty filter never hides them (the pages
      // also hide that filter control in skill mode).
      const difficultyMatch =
        node.data.isSkillNode || !filters.difficulty || node.data.difficulty === filters.difficulty
      const searchMatch = !hasSearch || (node.data.skill || '').toLowerCase().includes(searchLower)
      const practiceMatch =
        !practiceFilter ||
        (practiceFilter === 'with' ? !!node.data.hasTutorPractice : !node.data.hasTutorPractice)
      const visible =
        (!hasFilters && !hasSearch) ||
        (statusMatch && domainMatch && difficultyMatch && searchMatch && practiceMatch)
      return {
        ...node,
        selected: node.id === selectedNodeId,
        style: { ...node.style, opacity: visible ? 1 : 0.1 }
      }
    })
  }, [rfNodes, filters, searchQuery, selectedNodeId])

  const visibleNodeIds = useMemo(
    () =>
      new Set(
        filteredNodes
          .filter((n) => n.type !== 'domainGroup' && (n.style?.opacity ?? 1) === 1)
          .map((n) => n.id)
      ),
    [filteredNodes]
  )

  // Edge visibility: prerequisite edges always show; related edges depend on the selected mode
  // (hidden / shown-for-selected-node / shown-dimmed). Dims edges whose endpoints are filtered out.
  const filteredEdges = useMemo(() => {
    return rfEdges
      .map((edge) => {
        const isRelated = edge.data?.edgeType === 'related' || edge.id?.startsWith('related::')
        if (isRelated) {
          if (edgeVisibility === 'prerequisites') return null
          if (edgeVisibility === 'selected') {
            const connected = edge.source === selectedNodeId || edge.target === selectedNodeId
            if (!connected) return null
            return { ...edge, style: { ...edge.style, opacity: 0.7 } }
          }
          // 'all' — show related edges dimmed
          return { ...edge, style: { ...edge.style, opacity: 0.15 } }
        }
        return edge
      })
      .filter(Boolean)
      .map((edge) => ({
        ...edge,
        style: {
          ...edge.style,
          opacity:
            visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
              ? (edge.style?.opacity ?? 1)
              : 0.1
        }
      }))
  }, [rfEdges, edgeVisibility, selectedNodeId, visibleNodeIds])

  const onNodeMouseEnter = useCallback(
    (_, node) => {
      if (node.type !== 'domainGroup') onNodeHover?.(node.data)
    },
    [onNodeHover]
  )
  const onNodeMouseLeave = useCallback(() => onNodeHover?.(null), [onNodeHover])

  // Persist a dragged node's position (only in edit mode with a storage key; groups aren't moved).
  const handleNodeDragStop = useCallback(
    (_, node) => {
      if (!editable || !layoutStorageKey || node.type === 'domainGroup') return
      try {
        layoutCacheRef.current[node.id] = { x: node.position.x, y: node.position.y }
        localStorage.setItem(
          modeKey(layoutStorageKey, layoutMode, isSkillGraph),
          JSON.stringify(layoutCacheRef.current)
        )
      } catch {
        /* ignore persistence errors */
      }
    },
    [editable, layoutStorageKey, layoutMode, isSkillGraph]
  )

  const minimapColor = useCallback((node) => {
    if (node.type === 'domainGroup') return 'transparent'
    const status = node.data?.mastery?.status || 'UNVISITED'
    return STATUS_COLORS[status]?.dot || '#9CA3AF'
  }, [])

  if (isLayouting) {
    return (
      // Shared chrome offset (--map-chrome-h) is set on the EducatorKnowledgeMap root (from its
      // MAP_CHROME_H constant) and inherits here; the 270px fallback (keep in sync with MAP_CHROME_H)
      // keeps this correct if the canvas is ever rendered on its own.
      <div
        className="w-full min-h-[400px] bg-white rounded-lg border flex items-center justify-center"
        style={{ height: 'calc(100dvh - var(--map-chrome-h, 270px))' }}
      >
        <div className="flex items-center gap-2 text-gray-500">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Arranging graph...</span>
        </div>
      </div>
    )
  }

  return (
    // Shared chrome offset (--map-chrome-h); see the loading branch above.
    <div
      className="w-full min-h-[400px] bg-white rounded-lg border"
      style={{ height: 'calc(100dvh - var(--map-chrome-h, 270px))' }}
    >
      <ReactFlow
        nodes={filteredNodes}
        edges={filteredEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={editable}
        nodesConnectable={false}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onNodeDragStop={handleNodeDragStop}
        onNodeClick={(_, node) => {
          if (node.type !== 'domainGroup') onNodeClick?.(node.data)
        }}
        onPaneClick={() => onPaneClick?.()}
        fitView
        fitViewOptions={{ padding: 0.4 }}
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#f1f5f9" gap={20} />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={minimapColor}
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="!bg-white/80 !border !shadow-md"
        />
      </ReactFlow>
    </div>
  )
}
